import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Tag, Typography, Checkbox, DatePicker, InputNumber, Button, Spin, Divider, theme } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { IconBriefcase } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import LandingLayout from '../../components/Layouts/LandingLayout';
import EmptyState from '../../components/EmptyState';
import BackButton from '../../components/BackButton';
import { BookingPaymentStep } from '../../components/HeavyDutyMachineryBookingWidget';
import { IBookingSummaryData } from '../../components/BookingLinesSummary';
import { useAuth } from '../../contextProviders/AuthProvider';
import { getPublicService, createServiceBooking } from '../../services/ServiceService';
import { IServiceDetail, ICreateServiceBookingResponse } from '../../interfaces/heavyDutyMachinery/IService';
import { CANCELLATION_POLICY_OPTIONS } from '../../constants/heavyDutyMachinerySpaceOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { CORAL, CORAL_SOFT_BG } from '../../theme/creamyTokens';

const { Title, Text } = Typography;

const DEFAULT_HOURLY_DURATION = 1;
const DEFAULT_FIXED_JOB_DURATION = 2;

type LoadState = 'ok' | 'notFound' | 'notAvailable' | 'error';

/**
 * Guest-facing Service detail + checkout -- public (same LandingLayout/
 * AppLayout split as HeavyDutyMachineryDetail), but booking itself is gated
 * on isAuthenticated at the moment of action, same "anyone can browse,
 * identity only required for the actual action" stance
 * HeavyDutyMachineryCard's own favorite button already uses. Reuses
 * BookingPaymentStep (see that component's own doc comment) for the actual
 * Stripe Payment Element step -- no separate payment flow invented here.
 *
 * No public browse/search page links here anymore -- ServicesSearch/
 * ServiceSearchCard were removed (Service discovery is moving to the map,
 * not a separate search page, see the Fase 13 architecture doc); this page
 * is only reached today via a direct link or ServiceLine references
 * elsewhere (e.g. a past booking).
 */
const ServiceDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [service, setService] = useState<IServiceDetail | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('ok');

  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());
  // ?startAt= carries the dates over from wherever the guest came from --
  // today that is the "operators near this job" sheet on their own machinery
  // booking (see NearbyOperatorsSection), which already knows exactly when
  // the machine is theirs. Seeding the picker rather than replacing it: the
  // guest can still change it, and a link with no parameter (or a malformed
  // one) simply starts empty, the same as arriving here directly.
  const [searchParams] = useSearchParams();
  const startAtParam = searchParams.get('startAt');
  const seededStartAt = useMemo(() => {
    if (!startAtParam) return null;
    const parsed = dayjs(startAtParam);
    return parsed.isValid() && parsed.isAfter(dayjs()) ? parsed : null;
  }, [startAtParam]);
  const [startAt, setStartAt] = useState<Dayjs | null>(seededStartAt);
  const [durationHours, setDurationHours] = useState<number>(DEFAULT_HOURLY_DURATION);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<ICreateServiceBookingResponse | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    getPublicService(id)
      .then((data) => {
        setService(data);
        setDurationHours(data.pricingMode === 'FixedJob' ? DEFAULT_FIXED_JOB_DURATION : DEFAULT_HOURLY_DURATION);
      })
      .catch((error: Error) => {
        if (error.message.startsWith('HTTP Error: 404')) {
          // Bare 404, no message body -- FeatureFlagKeys.ServicesEnabled is off.
          setLoadState('notAvailable');
        } else if (error.message === 'Service not found') {
          setLoadState('notFound');
        } else {
          setLoadState('error');
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const isFixedJob = service?.pricingMode === 'FixedJob';
  // Instant is a diary booking and keeps today's flow untouched. Quote means
  // the price is a conclusion rather than an input, so there is nothing to
  // pick and nothing to pay yet -- see Service.RequestMode.
  const isQuoteOnly = service?.requestMode === 'Quote';
  const chargesVisitFee = !!service?.providerChargesVisitFee && (service?.providerVisitFeeAmount ?? 0) > 0;
  const formatPrice = (amount: number) => (service ? formatCurrency(amount, service.hostCurrencyCode, i18n.language) : '');

  const baseAmount = service
    ? isFixedJob
      ? service.fixedJobPrice ?? 0
      : (service.hourlyRate ?? 0) * durationHours
    : 0;
  const addOnsTotal = service ? service.addOns.filter((a) => selectedAddOnIds.has(a.id)).reduce((sum, a) => sum + a.price, 0) : 0;
  const totalPreview = baseAmount + addOnsTotal;

  const toggleAddOn = (addOnId: string) => {
    setSelectedAddOnIds((prev) => {
      const next = new Set(prev);
      if (next.has(addOnId)) next.delete(addOnId);
      else next.add(addOnId);
      return next;
    });
  };

  const handleReserve = async () => {
    if (!service) return;
    if (!isAuthenticated) {
      message.info(translate(t, TRANSLATIONS.SERVICE_DETAIL_LOGIN_REQUIRED));
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      navigate('/login');
      return;
    }
    if (!startAt || startAt.isBefore(dayjs())) {
      message.error(translate(t, TRANSLATIONS.SERVICE_DETAIL_INVALID_RANGE_ERROR));
      return;
    }
    if (!waiverAccepted) {
      message.error(translate(t, TRANSLATIONS.SERVICE_DETAIL_WAIVER_REQUIRED_ERROR));
      return;
    }

    const endAt = startAt.add(durationHours, 'hour');
    setSubmitting(true);
    try {
      const result = await createServiceBooking({
        serviceId: Number(service.id),
        serviceAddOnIds: Array.from(selectedAddOnIds).map(Number),
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        liabilityWaiverAccepted: true,
      });
      setBookingResult(result);
    } catch (error: any) {
      const rawMessage = error.message || '';
      if (rawMessage.includes('just booked by someone else')) {
        message.error(translate(t, TRANSLATIONS.SERVICE_DETAIL_SLOT_TAKEN_ERROR));
      } else {
        message.error(rawMessage || translate(t, TRANSLATIONS.SERVICE_DETAIL_BOOKING_ERROR));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancellationLabel = service
    ? translate(t, CANCELLATION_POLICY_OPTIONS.find((o) => o.value === service.cancellationPolicy)?.labelKey ?? service.cancellationPolicy)
    : '';

  let content: React.ReactNode;
  if (loading) {
    content = (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  } else if (loadState === 'notAvailable') {
    content = (
      <EmptyState
        icon={<IconBriefcase size={24} />}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NOT_AVAILABLE_TITLE)}
        description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NOT_AVAILABLE_DESCRIPTION)}
      />
    );
  } else if (loadState === 'notFound' || loadState === 'error' || !service) {
    content = <EmptyState icon={<IconBriefcase size={24} />} title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_NOT_FOUND)} description="" />;
  } else if (bookingResult && service) {
    // Fase 13: one line (this Service, provider blind-marketplace-safe
    // display name), with every selected add-on nested as that line's own
    // subLines -- see BookingLinesSummary's own doc comment for why this is
    // the same component/shape the Machinery-with-attached-Services
    // checkout uses. The line's own amount is bookingResult.serviceLines[0]
    // (backend-authoritative, straight off the actual persisted BookingLine)
    // rather than the client-side baseAmount/addOnsTotal preview above --
    // those stay a live estimate for the guest to see before submitting,
    // never the number actually charged (see baseAmount's own doc comment
    // a few lines up about the PricingMode=Both quirk, §9.11).
    const summaryData: IBookingSummaryData = {
      headerTitle: service.title,
      startAt: (startAt ?? dayjs()).toISOString(),
      endAt: (startAt ?? dayjs()).add(durationHours, 'hour').toISOString(),
      rentalMode: 'Hourly',
      hostCurrencyCode: bookingResult.hostCurrencyCode,
      guestCurrencyCode: bookingResult.guestCurrencyCode,
      totalPrice: bookingResult.totalPrice,
      guestChargeAmount: bookingResult.guestChargeAmount,
      lines: [
        {
          key: 'service',
          label: service.title,
          providerLabel: service.providerDisplayName,
          amount: bookingResult.serviceLines[0]?.amount ?? bookingResult.totalPrice,
          subLines: service.addOns
            .filter((a) => selectedAddOnIds.has(a.id))
            .map((a) => ({ key: a.id, label: a.title, amount: a.price })),
        },
      ],
    };
    content = (
      <div style={{ width: '100%', maxWidth: 480 }}>
        <BookingPaymentStep
          clientSecret={bookingResult.clientSecret!}
          summary={summaryData}
          notice={null}
          onPaid={() => navigate(`/heavy-duty-machinery/booking-confirming?bookingId=${bookingResult.id}`)}
        />
      </div>
    );
  } else {
    content = (
      <div style={{ width: '100%', maxWidth: 720 }}>
        <div style={{ marginBottom: 8 }}>
          <BackButton fallbackPath="/heavy-duty-machinery" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Avatar size={48} src={service.providerProfilePhotoUrl} icon={<IconBriefcase size={20} />} />
          <div>
            <Text strong>{service.providerDisplayName}</Text>
            {service.providerVerifiedBadge && (
              <Tag color="blue" icon={<CheckCircleFilled />} style={{ marginLeft: 8 }}>
                {translate(t, TRANSLATIONS.HOST_VERIFICATION_VERIFIED)}
              </Tag>
            )}
          </div>
        </div>

        <Tag style={{ borderRadius: 999, marginBottom: 8 }}>{service.serviceCategoryName}</Tag>
        <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
          {service.title}
        </Title>
        {service.description && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 20, whiteSpace: 'pre-wrap' }}>
            {service.description}
          </Text>
        )}

        <Divider />

        {/* Only the rate this checkout will actually charge -- for
            PricingMode "Both", BookingController.ComputeServiceLineAmount
            always bills hourly for a standalone booking (FixedJobPrice is
            never touched there), so showing FixedJobPrice here too would
            advertise a number this flow can't actually charge. */}
        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 20 }}>
          {isFixedJob
            ? `${translate(t, TRANSLATIONS.SERVICE_CARD_FIXED_JOB_PRICE_LABEL)}: ${formatPrice(service.fixedJobPrice ?? 0)}`
            : `${formatPrice(service.hourlyRate ?? 0)} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_HOUR)}`}
        </Text>

        {service.addOns.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ONS_TITLE)}
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {service.addOns.map((addOn) => (
                <Checkbox key={addOn.id} checked={selectedAddOnIds.has(addOn.id)} onChange={() => toggleAddOn(addOn.id)}>
                  {addOn.title} — {formatPrice(addOn.price)}
                </Checkbox>
              ))}
            </div>
          </div>
        )}

        {/* Everything from here down is the diary-booking flow, and it only
            appears for a listing that is actually sold that way. Under Quote
            the customer sees no calendar at all -- picking a day and a
            three-hour window for a trade job is doing the professional's work
            for them, and they will get it wrong. See ServiceRequest. */}
        {isQuoteOnly ? (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
              {translate(t, TRANSLATIONS.SERVICE_DETAIL_QUOTE_ONLY_HINT)}
            </Text>
            {/* Above the button, because it changes what pressing it means.
                The card already said it; this is the screen they decide on. */}
            {chargesVisitFee && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 14px',
                  borderRadius: 14,
                  background: CORAL_SOFT_BG,
                  color: CORAL,
                  fontSize: 13,
                }}
              >
                {translate(t, TRANSLATIONS.SERVICE_VISIT_FEE_NOTICE, {
                  amount: formatPrice(service.providerVisitFeeAmount!),
                })}
              </div>
            )}
            <Button
              type="primary"
              shape="round"
              size="large"
              block
              onClick={() => navigate(`/heavy-duty-machinery/services/${service.id}/request-quote`)}
            >
              {translate(t, TRANSLATIONS.SERVICE_DETAIL_REQUEST_QUOTE_BUTTON)}
            </Button>
          </div>
        ) : (
        <>
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            {translate(t, TRANSLATIONS.SERVICE_DETAIL_START_LABEL)}
          </Text>
          <DatePicker
            showTime
            value={startAt}
            onChange={setStartAt}
            disabledDate={(date) => date.isBefore(dayjs().startOf('day'))}
            style={{ width: '100%', maxWidth: 280 }}
          />

          <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.SERVICE_DETAIL_DURATION_LABEL)}
            </Text>
            <InputNumber
              min={0.5}
              step={0.5}
              value={durationHours}
              onChange={(value) => setDurationHours(value ?? DEFAULT_HOURLY_DURATION)}
              addonAfter={translate(t, TRANSLATIONS.SERVICE_DETAIL_DURATION_HOURS_SUFFIX)}
              style={{ width: 180 }}
            />
            {isFixedJob && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                {translate(t, TRANSLATIONS.SERVICE_DETAIL_DURATION_FIXED_JOB_HINT)}
              </Text>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">{translate(t, TRANSLATIONS.SERVICE_DETAIL_CANCELLATION_POLICY_LABEL)}: </Text>
          <Text>{cancellationLabel}</Text>
        </div>

        {service.depositAmount != null && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_DEPOSIT_AMOUNT_LABEL)}: {formatPrice(service.depositAmount)}
            </Text>
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {translate(t, TRANSLATIONS.SERVICE_DETAIL_DEPOSIT_NOTE)}
            </Text>
          </div>
        )}

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <Text strong>{translate(t, TRANSLATIONS.SERVICE_DETAIL_PRICE_PREVIEW_LABEL)}</Text>
          <Text strong style={{ fontSize: 20, color: token.colorPrimary }}>
            {formatPrice(totalPreview)}
          </Text>
        </div>

        <Checkbox checked={waiverAccepted} onChange={(e) => setWaiverAccepted(e.target.checked)} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.SERVICE_DETAIL_WAIVER_LABEL)}
        </Checkbox>

        <Button type="primary" shape="round" size="large" block loading={submitting} onClick={handleReserve}>
          {translate(t, TRANSLATIONS.SERVICE_DETAIL_RESERVE_BUTTON)}
        </Button>
        </>
        )}
      </div>
    );
  }

  if (authLoading) return null;
  if (!isAuthenticated) return <LandingLayout>{content}</LandingLayout>;
  return <AppLayout>{content}</AppLayout>;
};

export default ServiceDetail;
